import { Track, type Room } from 'livekit-client';
import {
  enumerateMediaDevices,
  saveDevicePreference,
} from '@/hooks/use-media-devices';

/**
 * Camera / microphone acquisition is harder than the LiveKit one-liner
 * `room.localParticipant.setCameraEnabled(true, ...)` makes it look. Three
 * problems show up in the wild:
 *
 *   1. Browsers disagree on `error.name`. "Device is in use by another app"
 *      is `NotReadableError` in Chrome ("Timeout starting video source",
 *      "Could not start video source"), `NotReadableError` in Firefox
 *      ("Failed to allocate videosource"), and either `NotReadableError` or
 *      `AbortError` in Safari. Without a normaliser the UI ends up showing
 *      the user a raw browser string with no actionable guidance.
 *
 *   2. A saved `deviceId` that points to an unplugged device makes
 *      `getUserMedia` wait for an OS-level timeout (~10 seconds in Chrome
 *      on Windows) before failing, even though `enumerateDevices()` could
 *      have told us instantly that the device is gone.
 *
 *   3. Connect-time failures and toggle-time failures historically used
 *      different code paths with different fallback rules and different
 *      toast wording. Users got a silent fail at join time, then a raw
 *      browser string later when they clicked the camera icon.
 *
 * This module is the single chokepoint for enabling a camera or mic on a
 * LiveKit Room. It pre-flights the saved `deviceId`, classifies any error
 * into a closed set of reasons, and never throws — callers always handle
 * the same `EnableMediaResult` shape and translate the reason via
 * {@link describeMediaError} for a consistent toast across the app.
 */

export type MediaKind = 'camera' | 'microphone';

export type MediaErrorReason =
  /** User (or admin policy) blocked permission. */
  | 'permission'
  /** Device exists but the OS can't open it — almost always another app
   *  holding it (Zoom, Teams, OBS, another browser tab) or a transient
   *  driver hang right after the previous capture released. */
  | 'busy'
  /** No device matching the (possibly empty) constraints. Either the
   *  hardware is gone or the saved deviceId is stale. */
  | 'not-found'
  /** Browser does not implement `getUserMedia`. */
  | 'unsupported'
  /** Anything we couldn't classify. */
  | 'unknown';

export type EnableMediaResult =
  | {
      ok: true;
      /** The device the browser actually selected. May be `undefined` if
       *  the browser does not expose a deviceId for the chosen device
       *  (rare — happens on some mobile browsers). */
      deviceId: string | undefined;
      /** True when the user's saved preference was unavailable and we
       *  silently fell back to the system default. The caller usually
       *  surfaces a non-blocking toast in this case so the user knows
       *  why their dropdown selection isn't checked anymore. */
      fellBackToDefault: boolean;
    }
  | { ok: false; reason: MediaErrorReason; cause: unknown };

/**
 * Map a raw `getUserMedia` / `setCameraEnabled` / `setMicrophoneEnabled`
 * error onto a stable reason code. We check `error.name` first (the spec'd
 * field) and fall back to a message regex for the cases where browsers
 * disagree on the name.
 */
export function classifyMediaError(e: unknown): MediaErrorReason {
  const name = (e as { name?: string } | null)?.name ?? '';
  const msg = String((e as { message?: string } | null)?.message ?? '').toLowerCase();

  if (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    name === 'SecurityError' ||
    msg.includes('permission denied')
  ) {
    return 'permission';
  }
  // NotReadableError ≈ "device exists but can't be opened". Chrome on
  // Windows surfaces this as "Could not start video source" or "Timeout
  // starting video source"; Firefox as "Failed to allocate videosource";
  // Safari occasionally as AbortError. TrackStartError is LiveKit's
  // wrapper for the same condition.
  if (
    name === 'NotReadableError' ||
    name === 'TrackStartError' ||
    name === 'AbortError' ||
    /could not start|timeout starting|failed to allocate/.test(msg)
  ) {
    return 'busy';
  }
  if (
    name === 'NotFoundError' ||
    name === 'OverconstrainedError' ||
    name === 'ConstraintNotSatisfiedError' ||
    name === 'DevicesNotFoundError' ||
    /not found|requested device|no device/.test(msg)
  ) {
    return 'not-found';
  }
  if (
    name === 'NotSupportedError' ||
    (name === 'TypeError' && /getusermedia|media devices/.test(msg))
  ) {
    return 'unsupported';
  }
  return 'unknown';
}

/**
 * Translate a classified reason into a user-facing toast. Same copy is
 * used for connect-time, toggle-time, and switch-time failures so the
 * user sees consistent guidance regardless of where the failure happened.
 */
export function describeMediaError(
  reason: MediaErrorReason,
  kind: MediaKind,
): { title: string; description: string } {
  const dev = kind === 'camera' ? 'camera' : 'microphone';
  const Dev = kind === 'camera' ? 'Camera' : 'Microphone';
  switch (reason) {
    case 'permission':
      return {
        title: `${Dev} access blocked`,
        description: `Your browser is blocking ${dev} access for this site. Click the lock icon in the address bar, allow ${dev} access, then try again.`,
      };
    case 'busy':
      // The same OS-level timeout fires both when another app is genuinely
      // holding the device AND when the device driver has crashed / frozen
      // (a common Windows failure mode where the camera light is off, no
      // app is visibly using it, but the hardware is still locked). The
      // copy mentions both so users don't waste time hunting for an app
      // that doesn't exist before trying the actual fix (restart browser
      // / restart machine).
      return {
        title: `${Dev} is in use or unresponsive`,
        description:
          kind === 'camera'
            ? 'Another app (Zoom, Teams, OBS, etc.) may be using your camera, or your camera driver has frozen. Please close other video apps, or restart your browser or computer, then try again.'
            : 'Another app may be using your microphone, or your audio driver has frozen. Please close other video / call apps, or restart your browser or computer, then try again.',
      };
    case 'not-found':
      return {
        title: `No ${dev} found`,
        description: `We could not find a ${dev}. Please connect one and try again, or pick a different device from the menu.`,
      };
    case 'unsupported':
      return {
        title: `${Dev} not supported`,
        description: `Your browser does not support ${dev} access. Try the latest Chrome, Edge, Firefox, or Safari on desktop.`,
      };
    case 'unknown':
    default:
      return {
        title: `${Dev} error`,
        description: `Could not enable the ${dev}. Please try again or pick a different device from the menu.`,
      };
  }
}

/**
 * Verify that a saved `deviceId` still corresponds to a connected device.
 * When users unplug a USB camera or headset mid-session, browsers may keep
 * the old `deviceId` cached for a window and then make `getUserMedia` wait
 * for a long internal timeout before erroring with `OverconstrainedError`
 * or `NotFoundError`. Pre-flighting against `enumerateDevices()` lets us
 * fall back to the system default *immediately* and persist the cleanup.
 *
 * Returns the deviceId unchanged when it's still present, or `undefined`
 * when it's gone (and clears the persisted preference as a side effect)
 * or when enumeration is unavailable (in which case we leave the hint in
 * place rather than silently dropping a working preference).
 */
async function validateAndPersist(
  kind: 'audioinput' | 'videoinput',
  deviceId: string | undefined,
): Promise<{ deviceId: string | undefined; droppedStalePreference: boolean }> {
  if (!deviceId) return { deviceId: undefined, droppedStalePreference: false };
  const list = await enumerateMediaDevices();
  // `enumerateDevices()` returns [] when called pre-permission. Don't drop
  // the preference in that case — we'd just lose a working choice.
  if (list.length === 0) return { deviceId, droppedStalePreference: false };
  const exists = list.some((d) => d.kind === kind && d.deviceId === deviceId);
  if (exists) return { deviceId, droppedStalePreference: false };
  saveDevicePreference(kind, null);
  return { deviceId: undefined, droppedStalePreference: true };
}

/**
 * Internal helper that wraps a LiveKit `setXEnabled(true, opts)` call with
 * pre-flight validation, classified-error handling, and a stale-deviceId
 * retry against the system default.
 *
 * Note on retrying `busy`: we deliberately do NOT retry on `busy`. Two
 * reasons:
 *   1. The first attempt has already paid the OS-level ~10s timeout, so a
 *      retry would compound to a ~20s wait before the user sees an error.
 *   2. If the camera is genuinely held by another app, the second attempt
 *      will fail the same way, with the same long wait.
 * The friendly "Camera is in use" message is the actual UX fix for that
 * case — telling the user to close Zoom / Teams / OBS and try again.
 */
async function enableTrack(
  enable: (opts: { deviceId: string } | undefined) => Promise<unknown>,
  readActualDeviceId: () => string | undefined,
  preferredDeviceId: string | undefined,
  kind: 'audioinput' | 'videoinput',
): Promise<EnableMediaResult> {
  const { deviceId: validatedId, droppedStalePreference } = await validateAndPersist(
    kind,
    preferredDeviceId,
  );

  const tryEnable = async (deviceId: string | undefined): Promise<void> => {
    await enable(deviceId ? { deviceId } : undefined);
  };

  let firstError: unknown;
  try {
    await tryEnable(validatedId);
    return {
      ok: true,
      deviceId: readActualDeviceId() ?? validatedId,
      fellBackToDefault: droppedStalePreference,
    };
  } catch (e) {
    firstError = e;
  }

  const reason = classifyMediaError(firstError);

  // Stale device id that survived our pre-flight (e.g. enumerateDevices
  // returned the device but it's actually gone). Drop the preference and
  // retry once with the system default.
  if (reason === 'not-found' && validatedId) {
    saveDevicePreference(kind, null);
    try {
      await tryEnable(undefined);
      return { ok: true, deviceId: readActualDeviceId(), fellBackToDefault: true };
    } catch (e2) {
      return { ok: false, reason: classifyMediaError(e2), cause: e2 };
    }
  }

  return { ok: false, reason, cause: firstError };
}

/**
 * Enable the camera on a LiveKit Room. Always resolves — never throws.
 * On failure, returns a classified `MediaErrorReason` the caller can
 * translate via {@link describeMediaError}.
 */
export async function enableCameraOnRoom(
  room: Room,
  preferredDeviceId: string | undefined,
): Promise<EnableMediaResult> {
  return enableTrack(
    (opts) => room.localParticipant.setCameraEnabled(true, opts),
    () =>
      room.localParticipant
        .getTrackPublication(Track.Source.Camera)
        ?.track?.mediaStreamTrack?.getSettings().deviceId,
    preferredDeviceId,
    'videoinput',
  );
}

/**
 * Enable the microphone on a LiveKit Room. Always resolves — never throws.
 * On failure, returns a classified `MediaErrorReason` the caller can
 * translate via {@link describeMediaError}.
 */
export async function enableMicrophoneOnRoom(
  room: Room,
  preferredDeviceId: string | undefined,
): Promise<EnableMediaResult> {
  return enableTrack(
    (opts) => room.localParticipant.setMicrophoneEnabled(true, opts),
    () =>
      room.localParticipant
        .getTrackPublication(Track.Source.Microphone)
        ?.track?.mediaStreamTrack?.getSettings().deviceId,
    preferredDeviceId,
    'audioinput',
  );
}
